import React, { forwardRef, PropsWithChildren, useCallback, useImperativeHandle, useState } from 'react';
import { CheckCircleOutlined } from '@ant-design/icons';
import { Button, Timeline } from 'antd';
import debug from 'debug';
import { Icons } from 'icons';
import { useRouter } from 'views/useRouter';

import { classnames } from 'lib/classnames';
import useI18n from 'lib/hooks/useI18n';
import { useInitialRecoilState } from 'lib/useInitialRecoilState';

import { StepPageProps, StepPageRefProps } from './type';

import styles from './index.module.less';

const cx = classnames(styles);
const log = debug('itemMoveLayout:page');

export enum STEP_INDEX {
  SELECT_WORKSPACE_AND_ITEMTYPE = 1,
  CHECK_FIELD_CHANGE = 2,
  CONFIRM_FIELD_CHANGE = 3,
  FINISH = 4,
}

const TargetColors = {
  green: 'green',
  blue: 'blue',
  gray: 'gray',
};

const StepLayout: React.ForwardRefRenderFunction<StepPageRefProps, PropsWithChildren<StepPageProps>> = (
  {
    header,
    error,
    title,
    step,
    stepList,
    children,
    onStepChange,
    beforeStep,
    updateResult,
    canNext,
    startIndex = STEP_INDEX.SELECT_WORKSPACE_AND_ITEMTYPE,
  },
  ref,
) => {
  useInitialRecoilState();
  const { t } = useI18n();
  const router = useRouter();
  const [stepLoading, setStepLoading] = useState(false);

  // 根据当前步骤，获取时间轴的颜色
  function getTimeLineColor(target: number) {
    if (step.skip) {
      return TargetColors.gray;
    }
    if (target < step.index) {
      return TargetColors.green;
    } else if (target === step.index) {
      return TargetColors.blue;
    } else {
      return TargetColors.gray;
    }
  }

  const goLast = useCallback(() => {
    setStepLoading(true);
    try {
      if (step.index <= startIndex) return;
      const getLastStep = index => stepList.find(stepItem => stepItem.index === index - 1);
      let lastStep = getLastStep(step.index);
      if (!lastStep) return;
      while (lastStep.index > startIndex && lastStep.skip) {
        lastStep = getLastStep(lastStep.index);
      }
      onStepChange(lastStep);
    } catch (error) {
      console.error(error.message);
    } finally {
      setStepLoading(false);
    }
  }, [onStepChange, startIndex, step.index, stepList]);

  const goNext = useCallback(async () => {
    setStepLoading(true);
    try {
      const result = await beforeStep?.();
      if (step.index > stepList.length) return;
      const getNextStep = index => stepList.find(stepItem => stepItem.index === index + 1);
      let nextStep = getNextStep(step.index);
      if (!nextStep) return;
      while (nextStep.index < stepList.length && nextStep.skip) {
        nextStep = getNextStep(nextStep.index);
      }
      onStepChange(nextStep);
      log('next before result', result);
      updateResult({ status: result.status, message: result.message, data: result.data });
    } catch (error) {
      console.error(error.message);
      log('next result error', error.message);
      updateResult({ status: 'error', message: error.message });
    } finally {
      setStepLoading(false);
    }
  }, [beforeStep, onStepChange, step.index, stepList, updateResult]);

  useImperativeHandle(
    ref,
    () => {
      return {
        jumpStep: (index: number) => {
          const next = stepList.find(stepItem => stepItem.index === index);
          onStepChange(next);
        },
      };
    },
    [onStepChange, stepList],
  );

  const back = useCallback(() => {
    router.back();
  }, [router]);

  return (
    <div className={cx('content-wrap')}>
      <div className={cx('left-wrapper')}>
        <p className={cx('header')}>{title}</p>
        <Timeline>
          {stepList.map((item, index) => (
            <Timeline.Item
              dot={
                index < step.index - 1 ? (
                  <CheckCircleOutlined className={cx('time-line-dot', 'check')} />
                ) : (
                  <span className={cx({ active: index < step.index, 'time-line-dot': true })}>{index + 1}</span>
                )
              }
              key={index}
              color={getTimeLineColor(index + 1)}
            >
              <span className={cx({ disabled: step.skip, 'time-line-text': true })}>{item.text}</span>
            </Timeline.Item>
          ))}
        </Timeline>
      </div>
      <div className={cx('right-wrapper')}>
        <div className={cx('right-content')}>
          {header}
          {children}
          <div className={cx('step-btn-wrap')}>
            {step.index > STEP_INDEX.SELECT_WORKSPACE_AND_ITEMTYPE && !step.hideText && !stepLoading && (
              <Button className={cx('last-step-btn')} onClick={goLast}>
                {t('pages.itemMove.lastStep')}
              </Button>
            )}
            {!step.hideText && (
              <>
                <Button
                  disabled={!canNext}
                  loading={stepLoading}
                  className={cx('next-step-btn')}
                  type="primary"
                  onClick={goNext}
                >
                  {step.nextText || t('pages.itemMove.nextStep')}
                </Button>
                {error}
              </>
            )}
          </div>
          <div>
            <p className={cx('tip-msg')}>{t('pages.itemMove.tipMsg')}</p>
          </div>
        </div>
        <div className={cx('close-wrapper')} onClick={back}>
          <Icons.Close />
        </div>
      </div>
    </div>
  );
};

export default forwardRef(StepLayout);
